import { Injectable, Injector } from '@angular/core';
import { Observable, forkJoin } from 'rxjs';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs/operators';
import { BaseService } from '../../shared/services/base.service';
import { IPageApiResponse, IPageResponse } from '../interfaces/IPageResponse';
import { IGetProviderForViewOutput, IProvider, IProviderCount } from '../interfaces/IProvider';
import { ISpecialty } from '../interfaces/ISpecialty';
import { ILookup } from '../interfaces/ILookup';
import { ILocation } from '../interfaces/ILocation';
import { INearbyLocation } from '../interfaces/INearbyLocation';
import { IPlaceOfService } from '../interfaces/IPlaceOfService';
import { ILocationGroup, IGetLocationGroupForViewDto } from '../interfaces/ILocationGroup';
import { INetworkStatus, ICreateOrEditNetworkStatusDto } from '../interfaces/INetworkStatus';
import { ILocationGroupMember, ICreateOrEditLocationGroupMemberDto } from '../interfaces/ILocationGroupMember';
import { IPatientCoverage, ICreateOrEditPatientCoverageDto } from '../interfaces/IPatientCoverage';
import { IPatient, ICreateOrEditPatientDto, IGetPatientForViewDto, IGetPatientForEditOutput } from '../interfaces/IPatient';
import { 
  PROVIDER, PROVIDER_ENDPOINTS, 
  LOOKUPS, LOOKUPS_ENDPOINTS, 
  SPECIALTY, SPECIALTY_ENDPOINTS,
  LOCATION, LOCATION_ENDPOINTS,
  CPT_CODE, CPT_CODE_ENDPOINTS,
  ICD10_CODE, ICD10_CODE_ENDPOINTS,
  MODIFIER_CODE, MODIFIER_CODE_ENDPOINTS,
  PLACE_OF_SERVICE, PLACE_OF_SERVICE_ENDPOINTS,
  RELATIONSHIP_CODE, RELATIONSHIP_CODE_ENDPOINTS,
  GENDER_CODE, GENDER_CODE_ENDPOINTS,
  SERVICE_TYPE_CODE, SERVICE_TYPE_CODE_ENDPOINTS,
  REVENUE_CODE, REVENUE_CODE_ENDPOINTS,
  UNIT_TYPE_CODE, UNIT_TYPE_CODE_ENDPOINTS,
  TIMING, TIMING_ENDPOINTS,
  PAYOR, PAYOR_ENDPOINTS,
  PAYOR_MAPPING, PAYOR_MAPPING_ENDPOINTS,
  CLEARING_HOUSE, CLEARING_HOUSE_ENDPOINTS,
  LOCATION_GROUP, LOCATION_GROUP_ENDPOINTS,
  NETWORK_STATUS, NETWORK_STATUS_ENDPOINTS,
  LOCATION_GROUP_MEMBER, LOCATION_GROUP_MEMBER_ENDPOINTS,
  PATIENT_COVERAGE, PATIENT_COVERAGE_ENDPOINTS,
  PATIENT, PATIENT_ENDPOINTS,
  INSURANCE_PLAN, INSURANCE_PLAN_ENDPOINTS,
  ELIGIBILITY_REQUEST, ELIGIBILITY_REQUEST_ENDPOINTS,
  ELIGIBILITY_RESPONSE, ELIGIBILITY_RESPONSE_ENDPOINTS,
    ELIGIBILITY_RESPONSE_BENEFIT, ELIGIBILITY_RESPONSE_BENEFIT_ENDPOINTS,
    ELIGIBILITY_REQUEST_STATUS, ELIGIBILITY_REQUEST_STATUS_ENDPOINTS
} from '../endpoints/pliny-endpoints.enum';
import { IInsurancePlan, ICreateOrEditInsurancePlanDto, IInsurancePayor, IPayorMapping, ICreateOrEditPayorMappingDto, IGetPayorMappingForViewDto, IGetPayorMappingForEditOutput } from '../interfaces/IInsurancePlan';
import { IEligibilityRequest, ICreateOrEditEligibilityRequestDto } from '../interfaces/IEligibilityRequest';
import { 
  IEligibilityResponse, 
  ICreateOrEditEligibilityResponseDto, 
  IEligibilityResponseBenefit, 
  ICreateOrEditEligibilityResponseBenefitDto 
} from '../interfaces/IEligibilityResponse';
import {
    IEligibilityRequestStatus,
    ICreateOrEditEligibilityRequestStatusDto
} from '../interfaces/IEligibilityRequestStatus';

@Injectable({
  providedIn: 'root'
})
export class PlinyService extends BaseService {
  
  constructor(injector: Injector) {
    super(injector);
  }

    override get apiUrl(): string {
        return `${environment.API_Document}`
    }

    // Generic helper method for all paginated APIs
    private getPaginatedData<T>(
        controller: string, 
        endpoint: string, 
        queryParams?: string, 
        itemKey?: string
    ): Observable<IPageResponse<T>> {
        const params = queryParams ? `?${queryParams}` : '';
        return this.get(`/${controller}/${endpoint}${params}`)
            .pipe(
                map((response: IPageApiResponse<any>) => ({
                    totalCount: response.result.totalCount,
                    items: response.result.items.map((item: any) => itemKey ? (item[itemKey] || item) : item)
                }))
            );
    }

    getProviders(queryParams: string): Observable<IPageResponse<IProvider>> {
        return this.getPaginatedData<IProvider>(PROVIDER.Provider, PROVIDER_ENDPOINTS.GetAll, queryParams, 'provider');
    }

    deleteProvider(id: string): Observable<void> {
        return this.delete(`/${PROVIDER.Provider}/${PROVIDER_ENDPOINTS.Delete}?Id=${id}`);
    }

    getProviderCount(): Observable<IProviderCount> {
        return this.get(`/${PROVIDER.Provider}/${PROVIDER_ENDPOINTS.GetProviderCount}`)
            .pipe(
                map((response: any) => {
                    return response?.result || response || { activeProvide: 0, totalProvider: 0 };
                })
            );
    }

    getLookup(name: string, queryParams?: string): Observable<any> {
        let url = `/${LOOKUPS.Lookups}/${LOOKUPS_ENDPOINTS.GetLookup}?name=${name}`;
        
        // Parse pagination parameters from queryParams
        let skipCount = 0;
        let maxResultCount = 25;
        
        if (queryParams) {
            // Extract SkipCount and MaxResultCount from queryParams
            const skipMatch = queryParams.match(/SkipCount=(\d+)/i);
            const maxMatch = queryParams.match(/MaxResultCount=(\d+)/i);
            
            if (skipMatch) {
                skipCount = parseInt(skipMatch[1], 10) || 0;
            }
            if (maxMatch) {
                maxResultCount = parseInt(maxMatch[1], 10) || 25;
            }
            
            // Ensure MaxResultCount is at least 25
            maxResultCount = Math.max(maxResultCount, 25);
            
            // Still append queryParams to URL (in case backend supports it)
            url += `&${queryParams}`;
        }
        
        return this.get(url)
            .pipe(
                map((response: any) => {
                    // Check if response has pagination structure (result.items and result.totalCount)
                    if (response && response.result) {
                        if (response.result.items && Array.isArray(response.result.items)) {
                            // Paginated response from backend
                            return {
                                totalCount: response.result.totalCount || response.result.items.length,
                                items: response.result.items as ILookup[]
                            };
                        } else if (Array.isArray(response.result)) {
                            // Non-paginated response (array directly) - apply frontend pagination
                            const allItems = response.result as ILookup[];
                            const sortedItems = [...allItems].sort((a, b) => (a.sequenceOrder ?? Infinity) - (b.sequenceOrder ?? Infinity));
                            const totalCount = sortedItems.length;
                            // Slice the array based on SkipCount and MaxResultCount
                            const startIndex = skipCount;
                            const endIndex = skipCount + (name === 'USStateName' ? 1000 : maxResultCount);
                            const paginatedItems = sortedItems.slice(startIndex, endIndex);
                            
                            return {
                                totalCount: totalCount,
                                items: paginatedItems
                            };
                        }
                    }
                    return { totalCount: 0, items: [] };
                })
            );
    }

    getSpecialties(queryParams?: string): Observable<IPageResponse<ISpecialty>> {
        return this.getPaginatedData<ISpecialty>(SPECIALTY.Specialty, SPECIALTY_ENDPOINTS.GetAll, queryParams, 'specialty');
    }

    getLocations(queryParams?: string): Observable<IPageResponse<ILocation>> {
        return this.getPaginatedData<ILocation>(LOCATION.Locations, LOCATION_ENDPOINTS.GetAll, queryParams, 'location');
    }

    deleteLocation(id: string): Observable<void> {
        return this.delete(`/${LOCATION.Locations}/${LOCATION_ENDPOINTS.Delete}?Id=${id}`);
    }

    getPayors(queryParams?: string): Observable<IPageResponse<any>> {
        return this.getPaginatedData(PAYOR.Payor, PAYOR_ENDPOINTS.GetAll, queryParams, 'payor');
    }

    getClearingHouses(queryParams?: string): Observable<IPageResponse<any>> {
        return this.getPaginatedData(CLEARING_HOUSE.ClearingHouse, CLEARING_HOUSE_ENDPOINTS.GetAll, queryParams, 'clearingHouse');
    }

    // Lookup APIs
    getCptCodes(queryParams: string): Observable<IPageResponse<any>> {
        return this.getPaginatedData(CPT_CODE.CptCode, CPT_CODE_ENDPOINTS.GetAll, queryParams, 'cptCode');
    }

    getIcd10Codes(queryParams: string): Observable<IPageResponse<any>> {
        return this.getPaginatedData(ICD10_CODE.Icd10Code, ICD10_CODE_ENDPOINTS.GetAll, queryParams, 'icd10Code');
    }

    getCptAndIcd10Codes(queryParams?: string): Observable<{ cptCodes: IPageResponse<any>; icd10Codes: IPageResponse<any> }> {
        const params = queryParams || 'MaxResultCount=1000';
        return forkJoin({
            cptCodes: this.getCptCodes(params),
            icd10Codes: this.getIcd10Codes(params)
        });
    }

    getModifierCodes(queryParams: string): Observable<IPageResponse<any>> {
        return this.getPaginatedData(MODIFIER_CODE.ModifierCode, MODIFIER_CODE_ENDPOINTS.GetAll, queryParams, 'modifierCode');
    }

    getPlaceOfServices(queryParams?: string): Observable<IPageResponse<IPlaceOfService>> {
        const params = queryParams ? `?${queryParams}` : '';
        return this.get(`/${PLACE_OF_SERVICE.PlaceOfService}/${PLACE_OF_SERVICE_ENDPOINTS.GetAll}${params}`)
            .pipe(
                map((response: IPageApiResponse<any>) => ({
                    totalCount: response.result.totalCount,
                    items: response.result.items.map((item: any) => {
                        const pos = item.placeOfService || item;
                        return {
                            ...pos,
                            name: pos.description || pos.name // Map description to name for consistency
                        } as IPlaceOfService;
                    })
                }))
            );
    }

    getRelationshipCodes(queryParams: string): Observable<IPageResponse<any>> {
        return this.getPaginatedData(RELATIONSHIP_CODE.RelationshipCode, RELATIONSHIP_CODE_ENDPOINTS.GetAll, queryParams, 'relationshipCode');
    }

    getGenderCodes(queryParams: string): Observable<IPageResponse<any>> {
        return this.getPaginatedData(GENDER_CODE.GenderCode, GENDER_CODE_ENDPOINTS.GetAll, queryParams, 'genderCode');
    }

    getServiceTypeCodes(queryParams: string): Observable<IPageResponse<any>> {
        return this.getPaginatedData(SERVICE_TYPE_CODE.ServiceTypeCode, SERVICE_TYPE_CODE_ENDPOINTS.GetAll, queryParams, 'serviceTypeCode');
    }

    getRevenueCodes(queryParams: string): Observable<IPageResponse<any>> {
        return this.getPaginatedData(REVENUE_CODE.RevenueCode, REVENUE_CODE_ENDPOINTS.GetAll, queryParams, 'revenueCode');
    }

    getUnitTypeCodes(queryParams: string): Observable<IPageResponse<any>> {
        return this.getPaginatedData(UNIT_TYPE_CODE.UnitTypeCode, UNIT_TYPE_CODE_ENDPOINTS.GetAll, queryParams, 'unitTypeCode');
    }


    createOrEditProvider(data: IProvider): Observable<IProvider> {
        return this.post('/Providers/CreateOrEdit', data).pipe(
            map((response: any) => {
                // Return the provider object from response (which should contain the ID)
                return response?.result || response || data;
            })
        );
    }

    getProviderForView(id: string): Observable<IProvider> {
        return this.get(`/Providers/GetProviderForView?id=${id}`)
            .pipe(
                map((response: { result: IGetProviderForViewOutput }) => {
                    return response?.result?.provider || null;
                })
            );
    }

    createOrEditLocation(data: ILocation): Observable<void> {
        return this.post('/Locations/CreateOrEdit', data);
    }

    getLocationForView(id: string): Observable<ILocation> {
        return this.get(`/Locations/GetLocationForView?id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.location || response?.location || response || null;
                })
            );
    }

    getNearbyLocations(origin: string, limit: number = 5): Observable<INearbyLocation[]> {
        return this.get(`/Locations/Nearby?origin=${encodeURIComponent(origin)}&limit=${limit}`)
            .pipe(
                map((response: any) => {
                    return response?.result || response || [];
                })
            );
    }

    // Location Closures CRUD operations
    getLocationClosures(queryParams?: string): Observable<any> {
        const url = queryParams 
            ? `/LocationClosures/GetAll?${queryParams}`
            : `/LocationClosures/GetAll`;
        return this.get(url)
            .pipe(
                map((response: any) => {
                    return response?.result || response || { items: [], totalCount: 0 };
                })
            );
    }

    createLocationClosure(locationId: string, data: any): Observable<string> {
        return this.post(`/LocationClosures/CreateClosure?locationId=${locationId}`, data)
            .pipe(
                map((response: any) => {
                    return response?.result || response || '';
                })
            );
    }

    updateLocationClosure(locationId: string, data: any): Observable<string> {
        // Update uses the same CreateClosure endpoint with id in the body
        return this.post(`/LocationClosures/CreateClosure?locationId=${locationId}`, data)
            .pipe(
                map((response: any) => {
                    return response?.result || response || '';
                })
            );
    }

    deleteLocationClosure(locationId: string, closureId: string): Observable<void> {
        return this.delete(`/LocationClosures/Delete?locationId=${locationId}&closureId=${closureId}`);
    }

    getClosuresOverview(queryParams?: string): Observable<any> {
        const params = queryParams ? `?${queryParams}` : '';
        return this.get(`/LocationClosures/GetClosuresOverview${params}`)
            .pipe(
                map((response: any) => {
                    return response?.result || response || null;
                })
            );
    }

    getClosureReasons(): Observable<string[]> {
        return this.get(`/LocationClosures/GetClosureReasons`)
            .pipe(
                map((response: any) => {
                    // Handle different response structures
                    if (Array.isArray(response)) {
                        return response;
                    }
                    if (response?.result?.reasons) {
                        return response.result.reasons;
                    }
                    if (response?.reasons) {
                        return response.reasons;
                    }
                    if (response?.result && Array.isArray(response.result)) {
                        return response.result;
                    }
                    return [];
                })
            );
    }

    // Location Groups CRUD operations
    getLocationGroups(queryParams?: string): Observable<IPageResponse<ILocationGroup>> {
        return this.getPaginatedData<ILocationGroup>(
            LOCATION_GROUP.LocationGroup,
            LOCATION_GROUP_ENDPOINTS.GetAll,
            queryParams,
            'locationGroup'
        );
    }

    getLocationGroupForView(id: string): Observable<ILocationGroup> {
        return this.get(`/${LOCATION_GROUP.LocationGroup}/${LOCATION_GROUP_ENDPOINTS.GetLocationGroupForView}?id=${id}`)
            .pipe(
                map((response: any) => {
                    // Handle different response structures
                    const locationGroup = response?.result?.locationGroup || response?.locationGroup || response;
                    return locationGroup || null;
                })
            );
    }

    createOrEditLocationGroup(data: ILocationGroup): Observable<void> {
        // Convert boolean isActive to integer (0/1) for API if needed
        let isActiveValue: number | boolean = data.isActive;
        if (typeof data.isActive === 'boolean') {
            isActiveValue = data.isActive ? 1 : 0;
        }
        const apiData: any = {
            ...data,
            isActive: isActiveValue
        };
        return this.post(`/${LOCATION_GROUP.LocationGroup}/${LOCATION_GROUP_ENDPOINTS.CreateOrEdit}`, apiData);
    }

    deleteLocationGroup(id: string): Observable<void> {
        return this.delete(`/${LOCATION_GROUP.LocationGroup}/${LOCATION_GROUP_ENDPOINTS.Delete}?Id=${id}`);
    }

    extractLocationFromUrl(data: { url?: string; address?: string; name?: string; shortName?: string; alias1?: string; alias2?: string; alias3?: string }): Observable<any> {
        return this.post('/Locations/extract-from-url', data)
            .pipe(
                map((response: any) => {
                    return response?.result || response || null;
                })
            );
    }

    extractProviderFromUrl(data: { url?: string; npi?: string; firstName?: string; lastName?: string }): Observable<any> {
        return this.post('/Providers/extract-from-url', data)
            .pipe(
                map((response: any) => {
                    return response?.result || response || null;
                })
            );
    }
    getProviderAlertCount(): Observable<any> {
        return this.get(`/ProviderAlerts/GetProviderAlertCount`)
            .pipe(
                map((response: any) => {
                    return response?.result || response || {};
                })
            );
    }

    getAllProviderAlerts(queryParams?: string): Observable<any> {
        const url = queryParams 
            ? `/ProviderAlerts/GetAll?${queryParams}`
            : `/ProviderAlerts/GetAll`;
        return this.get(url, { headers: { 'noloader': 'true' } })
            .pipe(
                map((response: any) => {
                    return response?.result || response || { items: [], totalCount: 0 };
                })
            );
    }

    getProviderAlertForView(id: string): Observable<any> {
        return this.get(`/ProviderAlerts/GetProviderAlertForView?id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result || response || {};
                })
            );
    }

    getProviderAlertForEdit(id: string): Observable<any> {
        return this.get(`/ProviderAlerts/GetProviderAlertForEdit?Id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result || response || {};
                })
            );
    }

    createOrEditProviderAlert(data: any): Observable<void> {
        return this.post(`/ProviderAlerts/CreateOrEdit`, data);
    }

    deleteProviderAlert(id: string): Observable<void> {
        return this.delete(`/ProviderAlerts/Delete?Id=${id}`);
    }

    // Provider Leaves APIs
    getProviderLeaves(queryParams?: string): Observable<any> {
        const url = queryParams 
            ? `/ProviderLeaves/GetAll?${queryParams}`
            : `/ProviderLeaves/GetAll`;
        return this.get(url)
            .pipe(
                map((response: any) => {
                    return response?.result || response || { items: [], totalCount: 0 };
                })
            );
    }

    getLeaveOverview(queryParams?: string): Observable<any> {
        const url = queryParams 
            ? `/ProviderLeaves/GetLeaveOverview?${queryParams}`
            : `/ProviderLeaves/GetLeaveOverview`;
        return this.get(url)
            .pipe(
                map((response: any) => {
                    return response?.result || response || { leaves: [], statistics: null, totalCount: 0 };
                })
            );
    }

    getLeaveReasons(): Observable<string[]> {
        return this.get(`/ProviderLeaves/GetLeaveReasons`)
            .pipe(
                map((response: any) => {
                    // Handle different response structures
                    if (Array.isArray(response)) {
                        return response;
                    } else if (response?.result && Array.isArray(response.result)) {
                        return response.result;
                    } else if (response?.reasons && Array.isArray(response.reasons)) {
                        return response.reasons;
                    } else {
                        return [];
                    }
                })
            );
    }

    createProviderLeave(data: any): Observable<void> {
        return this.post(`/ProviderLeaves/Create`, data);
    }

    updateProviderLeave(id: string, data: any): Observable<void> {
        return this.put(`/ProviderLeaves/update?Id=${id}`, data);
    }

    deleteProviderLeave(providerId: string, leaveId: string): Observable<void> {
        return this.delete(`/ProviderLeaves/Delete?providerId=${providerId}&leaveId=${leaveId}`);
    }

    // Provider Availability APIs
    getProviderAvailability(providerId: string): Observable<any> {
        return this.get(`/providers/${providerId}/availability`)
            .pipe(
                map((response: any) => {
                    return response?.result || response || null;
                })
            );
    }

    getProviderAvailabilityAsOf(providerId: string, locationId?: string, date?: string): Observable<any> {
        let url = `/providers/${providerId}/availability/as-of`;
        const params: string[] = [];
        if (locationId) params.push(`locationId=${locationId}`);
        if (date) params.push(`date=${encodeURIComponent(date)}`);
        if (params.length > 0) url += `?${params.join('&')}`;
        
        return this.get(url)
            .pipe(
                map((response: any) => {
                    return response?.result || response || [];
                })
            );
    }

    getProviderAvailabilityHistory(providerId: string, locationId?: string, date?: string): Observable<any> {
        let url = `/providers/${providerId}/availability/history`;
        const params: string[] = [];
        if (locationId) params.push(`locationId=${locationId}`);
        if (date) params.push(`date=${encodeURIComponent(date)}`);
        if (params.length > 0) url += `?${params.join('&')}`;
        
        return this.get(url)
            .pipe(
                map((response: any) => {
                    return response?.result || response || [];
                })
            );
    }

    changeProviderSchedule(providerId: string, locationId: string, data: any): Observable<any> {
        return this.post(`/providers/${providerId}/locations/${locationId}/availability/change`, data)
            .pipe(
                map((response: any) => {
                    return response?.result || response || [];
                })
            );
    }

    changeSchedule(providerId: string, locationId: string, schedule: any[]): Observable<any> {
        let url = `/Providers/ChangeSchedule?providerId=${providerId}&locationId=${locationId}`;
        return this.post(url, schedule)
            .pipe(
                map((response: any) => {
                    return response?.result || response || [];
                })
            );
    }

    closeProviderSchedule(providerId: string, locationId: string, effectiveTo: string): Observable<any> {
        let url = `/Providers/CloseSchedule?providerId=${providerId}&locationId=${locationId}&effectiveTo=${encodeURIComponent(effectiveTo)}`;
        return this.post(url, null)
            .pipe(
                map((response: any) => {
                    return response?.result || response || null;
                })
            );
    }

    // Provider Location Availability APIs
    getProviderLocationAvailabilities(filters?: string): Observable<any> {
        const url = filters ? `/ProviderLocationAvailabilitiesService/GetAll?${filters}` : '/ProviderLocationAvailabilitiesService/GetAll';
        return this.get(url)
            .pipe(
                map((response: any) => {
                    return response?.result || response || [];
                })
            );
    }

    getProviderLocationAvailabilityById(id: string): Observable<any> {
        return this.get(`/providerlocationavailability/${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result || response || null;
                })
            );
    }

    createProviderLocationAvailability(data: any): Observable<any> {
        return this.post('/providerlocationavailability', data)
            .pipe(
                map((response: any) => {
                    return response?.result || response || [];
                })
            );
    }

    updateProviderLocationAvailability(id: string, data: any): Observable<any> {
        return this.put(`/providerlocationavailability/${id}`, data)
            .pipe(
                map((response: any) => {
                    return response?.result || response || null;
                })
            );
    }

    deleteProviderLocationAvailability(id: string): Observable<any> {
        return this.delete(`/ProviderLocationAvailabilitiesService/Delete?id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result || response || null;
                })
            );
    }

    // Location Working Hours API
    getLocationWorkingHours(locationId: string): Observable<any[]> {
        return this.get(`/Locations/${locationId}/working-hours`)
            .pipe(
                map((response: any) => {
                    return response?.result || response || [];
                })
            );
    }

    // Location Availability APIs
    getLocationAvailabilities(queryParams?: string): Observable<any> {
        const url = queryParams 
            ? `/LocationAvailabilities/GetAll?${queryParams}`
            : `/LocationAvailabilities/GetAll`;
        return this.get(url)
            .pipe(
                map((response: any) => {
                    return response?.result || response || { items: [], totalCount: 0 };
                })
            );
    }

    getLocationAvailabilityForView(id: string): Observable<any> {
        return this.get(`/LocationAvailabilities/GetLocationAvailabilityForView?id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result || response || null;
                })
            );
    }

    getLocationAvailabilityForEdit(id: string): Observable<any> {
        return this.get(`/LocationAvailabilities/GetLocationAvailabilityForEdit?Id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result || response || null;
                })
            );
    }

    createLocationAvailability(data: any): Observable<void> {
        return this.post(`/LocationAvailabilities/Create`, data);
    }

    updateLocationAvailability(data: any, locationId?: string): Observable<void> {
        const url = locationId 
            ? `/LocationAvailabilities/ChangeSchedule?locationId=${locationId}`
            : `/LocationAvailabilities/ChangeSchedule`;
        return this.put(url, data);
    }

    deleteLocationAvailability(id: string): Observable<void> {
        return this.delete(`/LocationAvailabilities/Delete?Id=${id}`);
    }

    bulkCreateLocationAvailability(data: any[]): Observable<void> {
        return this.post(`/LocationAvailabilities/BulkCreate`, data);
    }

    getCurrentSchedule(locationId: string, queryParams?: string): Observable<any> {
        const params = queryParams ? `&${queryParams}` : '';
        return this.get(`/LocationAvailabilities/GetCurrentSchedule?locationId=${locationId}${params}`)
            .pipe(
                map((response: any) => {
                    return response?.result || response || null;
                })
            );
    }

    closeLocationSchedule(locationId: string, effectiveTo: string): Observable<any> {
        return this.post(`/LocationAvailabilities/CloseSchedule?locationId=${locationId}&effectiveTo=${encodeURIComponent(effectiveTo)}`, null)
            .pipe(
                map((response: any) => {
                    return response?.result || response || null;
                })
            );
    }

    getScheduleHistory(locationId: string, queryParams?: string): Observable<any> {
        const params = queryParams ? `&${queryParams}` : '';
        return this.get(`/LocationAvailabilities/GetScheduleHistory?locationId=${locationId}${params}`)
            .pipe(
                map((response: any) => {
                    return response?.result || response || [];
                })
            );
    }
    getLocationHolidays(queryParams?: string): Observable<any> {
        const url = queryParams 
            ? `/LocationHolidays/GetAll?${queryParams}`
            : `/LocationHolidays/GetAll`;
        return this.get(url)
            .pipe(
                map((response: any) => {
                    return response?.result || response || { items: [], totalCount: 0 };
                })
            );
    }
    getLocationHolidayForView(id: string): Observable<any> {
        return this.get(`/LocationHolidays/GetLocationHolidayForView?id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result || response || null;
                })
            );
    }
    getLocationHolidayForEdit(id: string): Observable<any> {
        return this.get(`/LocationHolidays/GetLocationHolidayForEdit?Id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result || response || null;
                })
            );
    }
    createOrEditLocationHoliday(data: any): Observable<void> {
        return this.post(`/LocationHolidays/CreateOrEdit`, data);
    }

    deleteLocationHoliday(id: string): Observable<void> {
        return this.delete(`/LocationHolidays/Delete?Id=${id}`);
    }
    // Network Status APIs
    getNetworkStatuses(queryParams?: string): Observable<IPageResponse<INetworkStatus>> {
        return this.getPaginatedData<INetworkStatus>(NETWORK_STATUS.NetworkStatuses, NETWORK_STATUS_ENDPOINTS.GetAll, queryParams, 'networkStatus');
    }

    getNetworkStatusForView(id: string): Observable<INetworkStatus> {
        return this.get(`/${NETWORK_STATUS.NetworkStatuses}/${NETWORK_STATUS_ENDPOINTS.GetNetworkStatusForView}?id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.networkStatus || response?.networkStatus || response?.result || response;
                })
            );
    }

    getNetworkStatusForEdit(id: string): Observable<ICreateOrEditNetworkStatusDto> {
        return this.get(`/${NETWORK_STATUS.NetworkStatuses}/${NETWORK_STATUS_ENDPOINTS.GetNetworkStatusForEdit}?Id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.networkStatus || response?.networkStatus || response?.result || response;
                })
            );
    }

    createOrEditNetworkStatus(data: ICreateOrEditNetworkStatusDto): Observable<void> {
        return this.post(`/${NETWORK_STATUS.NetworkStatuses}/${NETWORK_STATUS_ENDPOINTS.CreateOrEdit}`, data);
    }

    deleteNetworkStatus(id: string): Observable<void> {
        return this.delete(`/${NETWORK_STATUS.NetworkStatuses}/${NETWORK_STATUS_ENDPOINTS.Delete}?Id=${id}`);
    }

    // Location Group Members APIs
    getLocationGroupMembers(queryParams?: string): Observable<IPageResponse<ILocationGroupMember>> {
        return this.getPaginatedData<ILocationGroupMember>(LOCATION_GROUP_MEMBER.LocationGroupMembers, LOCATION_GROUP_MEMBER_ENDPOINTS.GetAll, queryParams, 'locationGroupMember');
    }

    getLocationGroupMemberForView(id: string): Observable<ILocationGroupMember> {
        return this.get(`/${LOCATION_GROUP_MEMBER.LocationGroupMembers}/${LOCATION_GROUP_MEMBER_ENDPOINTS.GetLocationGroupMemberForView}?id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.locationGroupMember || response?.locationGroupMember || response?.result || response;
                })
            );
    }

    getLocationGroupMemberForEdit(id: string): Observable<ICreateOrEditLocationGroupMemberDto> {
        return this.get(`/${LOCATION_GROUP_MEMBER.LocationGroupMembers}/${LOCATION_GROUP_MEMBER_ENDPOINTS.GetLocationGroupMemberForEdit}?Id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.locationGroupMember || response?.locationGroupMember || response?.result || response;
                })
            );
    }

    createOrEditLocationGroupMember(data: ICreateOrEditLocationGroupMemberDto): Observable<void> {
        return this.post(`/${LOCATION_GROUP_MEMBER.LocationGroupMembers}/${LOCATION_GROUP_MEMBER_ENDPOINTS.CreateOrEdit}`, data);
    }

    deleteLocationGroupMember(id: string): Observable<void> {
        return this.delete(`/${LOCATION_GROUP_MEMBER.LocationGroupMembers}/${LOCATION_GROUP_MEMBER_ENDPOINTS.Delete}?Id=${id}`);
    }

    // Patient Coverage APIs
    getPatientCoverages(queryParams?: string): Observable<IPageResponse<IPatientCoverage>> {
        return this.getPaginatedData<IPatientCoverage>(
            PATIENT_COVERAGE.PatientCoverages,
            PATIENT_COVERAGE_ENDPOINTS.GetAll,
            queryParams,
            'patientCoverage'
        );
    }

    getPatientCoverageForView(id: string): Observable<IPatientCoverage> {
        return this.get(`/${PATIENT_COVERAGE.PatientCoverages}/${PATIENT_COVERAGE_ENDPOINTS.GetPatientCoverageForView}?id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.patientCoverage || response?.patientCoverage || response?.result || response;
                })
            );
    }

    getPatientCoverageForEdit(id: string): Observable<ICreateOrEditPatientCoverageDto> {
        return this.get(`/${PATIENT_COVERAGE.PatientCoverages}/${PATIENT_COVERAGE_ENDPOINTS.GetPatientCoverageForEdit}?Id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.patientCoverage || response?.patientCoverage || response?.result || response;
                })
            );
    }

    createOrEditPatientCoverage(data: ICreateOrEditPatientCoverageDto): Observable<void> {
        return this.post(`/${PATIENT_COVERAGE.PatientCoverages}/${PATIENT_COVERAGE_ENDPOINTS.CreateOrEdit}`, data);
    }

    deletePatientCoverage(id: string): Observable<void> {
        return this.delete(`/${PATIENT_COVERAGE.PatientCoverages}/${PATIENT_COVERAGE_ENDPOINTS.Delete}?Id=${id}`);
    }

    uploadPatientCoverageAttachment(patientId: string, coverageId: string | null, formData: FormData): Observable<void> {
        let url = `/${PATIENT_COVERAGE.PatientCoverages}/${PATIENT_COVERAGE_ENDPOINTS.UploadAttachment}?PatientId=${patientId}`;
        if (coverageId) {
            url += `&CoverageId=${coverageId}`;
        }
        // Use post method directly - browser will set Content-Type with boundary automatically for FormData
        return this.post(url, formData);
    }

    uploadProviderFiles(providerId: string, formData: FormData, attachmentType: string = ''): Observable<boolean> {
        // Use doc_api since this is a file upload endpoint
        // Add providerid to FormData
        formData.append('providerid', providerId);
        // Add attachmentType to FormData if provided
        if (attachmentType) {
            formData.append('attachmentType', attachmentType);
        }
        return this.http.post<boolean>(`${this.doc_api}/providers/files/file`, formData);
    }

    getProviderAttachments(providerId: string): Observable<any[]> {
        // Use doc_api for this endpoint
        return this.http.get<any[]>(`${this.doc_api}/providers/file/attachment/${providerId}`);
    }

    downloadProviderAttachment(attachmentId: string): Observable<Blob> {
        // Use doc_api for this endpoint
        return this.http.get(`${this.doc_api}/providers/file/download/${attachmentId}`, {
            responseType: 'blob'
        });
    }

    // Patient File APIs
    uploadPatientFiles(patientId: string, formData: FormData, fileType: string = 'attachment'): Observable<boolean> {
        // Use doc_api for file upload endpoint
        // Add patientId and fileType to FormData
        formData.append('patientId', patientId);
        formData.append('attachmentType', fileType);
        return this.http.post<boolean>(`${this.doc_api}/patients/files/file`, formData);
    }

    getPatientAttachments(patientId: string): Observable<any[]> {
        // Use doc_api for this endpoint
        return this.http.get<any[]>(`${this.doc_api}/patients/file/attachment/${patientId}`);
    }

    downloadPatientFile(fileId: string): Observable<Blob> {
        // Use doc_api for this endpoint
        return this.http.get(`${this.doc_api}/patients/file/download/${fileId}`, {
            responseType: 'blob'
        });
    }

    deletePatientFile(fileId: string): Observable<void> {
        // Use doc_api for this endpoint
        return this.http.delete<void>(`${this.doc_api}/case/patients/${fileId}`);
    }

    // Patient APIs
    getPatients(queryParams?: string): Observable<IPageResponse<IPatient>> {
        return this.getPaginatedData<IPatient>(
            PATIENT.Patients,
            PATIENT_ENDPOINTS.GetAll,
            queryParams,
            'patient'
        );
    }

    getPatientForView(id: string): Observable<IGetPatientForViewDto> {
        return this.get(`/${PATIENT.Patients}/${PATIENT_ENDPOINTS.GetPatientForView}?id=${id}`)
            .pipe(
                map((response: any) => {
                    // Return full DTO with eligibility data
                    const result = response?.result || response;
                    return {
                        patient: result?.patient || result,
                        eligibilityResponse: result?.eligibilityResponse,
                        eligibilityResponseBenefit: result?.eligibilityResponseBenefit || []
                    };
                })
            );
    }

    getPatientForEdit(id: string): Observable<ICreateOrEditPatientDto> {
        return this.get(`/${PATIENT.Patients}/${PATIENT_ENDPOINTS.GetPatientForEdit}?Id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.patient || response?.patient || response?.result || response;
                })
            );
    }

    createOrEditPatient(data: ICreateOrEditPatientDto): Observable<void> {
        return this.post(`/${PATIENT.Patients}/${PATIENT_ENDPOINTS.CreateOrEdit}`, data);
    }

    deletePatient(id: string): Observable<void> {
        return this.delete(`/${PATIENT.Patients}/${PATIENT_ENDPOINTS.Delete}?Id=${id}`);
    }

    // Insurance Payor APIs
    getPayorForView(id: string): Observable<IInsurancePayor> {
        return this.get(`/${PAYOR.Payor}/GetPayorForView?id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.payor || response?.payor || response?.result || response;
                })
            );
    }

    // Insurance Plan APIs
    getInsurancePlans(queryParams?: string): Observable<IPageResponse<IInsurancePlan>> {
        return this.getPaginatedData<IInsurancePlan>(
            INSURANCE_PLAN.InsurancePlans,
            INSURANCE_PLAN_ENDPOINTS.GetAll,
            queryParams,
            'insurancePlan'
        );
    }

    getInsurancePlansByPayor(payorId: string, queryParams?: string): Observable<IPageResponse<IInsurancePlan>> {
        const params = queryParams ? `PayorIdFilter=${payorId}&${queryParams}` : `PayorIdFilter=${payorId}`;
        return this.getPaginatedData<IInsurancePlan>(
            INSURANCE_PLAN.InsurancePlans,
            INSURANCE_PLAN_ENDPOINTS.GetAll,
            params,
            'insurancePlan'
        );
    }

    getInsurancePlanForView(id: string): Observable<IInsurancePlan> {
        return this.get(`/${INSURANCE_PLAN.InsurancePlans}/${INSURANCE_PLAN_ENDPOINTS.GetInsurancePlanForView}?id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.insurancePlan || response?.insurancePlan || response?.result || response;
                })
            );
    }

    getInsurancePlanForEdit(id: string): Observable<ICreateOrEditInsurancePlanDto> {
        return this.get(`/${INSURANCE_PLAN.InsurancePlans}/${INSURANCE_PLAN_ENDPOINTS.GetInsurancePlanForEdit}?Id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.insurancePlan || response?.insurancePlan || response?.result || response;
                })
            );
    }

    createOrEditInsurancePlan(data: ICreateOrEditInsurancePlanDto): Observable<void> {
        return this.post(`/${INSURANCE_PLAN.InsurancePlans}/${INSURANCE_PLAN_ENDPOINTS.CreateOrEdit}`, data);
    }

    deleteInsurancePlan(id: string): Observable<void> {
        return this.delete(`/${INSURANCE_PLAN.InsurancePlans}/${INSURANCE_PLAN_ENDPOINTS.Delete}?Id=${id}`);
    }

    // PayorMapping APIs
    getPayorMappingForView(id: string): Observable<IGetPayorMappingForViewDto> {
        return this.get(`/${PAYOR_MAPPING.PayorMapping}/${PAYOR_MAPPING_ENDPOINTS.GetPayorMappingForView}?id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result || response;
                })
            );
    }

    getPayorMappingForEdit(id: string): Observable<IGetPayorMappingForEditOutput> {
        return this.get(`/${PAYOR_MAPPING.PayorMapping}/${PAYOR_MAPPING_ENDPOINTS.GetPayorMappingForEdit}?Id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result || response;
                })
            );
    }

    getPayorMappingByPayorId(payorId: string): Observable<IPayorMapping | null> {
        // Get all PayorMappings filtered by PayorId
        return this.getPaginatedData<IPayorMapping>(
            PAYOR_MAPPING.PayorMapping,
            PAYOR_MAPPING_ENDPOINTS.GetAll,
            `PayorIdFilter=${payorId}&MaxResultCount=1`,
            'payorMapping'
        ).pipe(
            map((response: IPageResponse<IPayorMapping>) => {
                if (response.items && response.items.length > 0) {
                    return response.items[0];
                }
                return null;
            })
        );
    }

    createOrEditPayorMapping(data: ICreateOrEditPayorMappingDto): Observable<void> {
        return this.post(`/${PAYOR_MAPPING.PayorMapping}/${PAYOR_MAPPING_ENDPOINTS.CreateOrEdit}`, data);
    }

    deletePayorMapping(id: string): Observable<void> {
        return this.delete(`/${PAYOR_MAPPING.PayorMapping}/${PAYOR_MAPPING_ENDPOINTS.Delete}?Id=${id}`);
    }

    // Eligibility Request APIs
    getEligibilityRequests(queryParams?: string): Observable<IPageResponse<IEligibilityRequest>> {
        return this.getPaginatedData<IEligibilityRequest>(
            ELIGIBILITY_REQUEST.EligibilityRequests,
            ELIGIBILITY_REQUEST_ENDPOINTS.GetAll,
            queryParams,
            'eligibilityRequest'
        );
    }

    getEligibilityRequestForView(id: string): Observable<IEligibilityRequest> {
        return this.get(`/${ELIGIBILITY_REQUEST.EligibilityRequests}/${ELIGIBILITY_REQUEST_ENDPOINTS.GetEligibilityRequestForView}?id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.eligibilityRequest || response?.eligibilityRequest || response?.result || response;
                })
            );
    }

    getEligibilityRequestForEdit(id: string): Observable<ICreateOrEditEligibilityRequestDto> {
        return this.get(`/${ELIGIBILITY_REQUEST.EligibilityRequests}/${ELIGIBILITY_REQUEST_ENDPOINTS.GetEligibilityRequestForEdit}?Id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.eligibilityRequest || response?.eligibilityRequest || response?.result || response;
                })
            );
    }

    createOrEditEligibilityRequest(data: ICreateOrEditEligibilityRequestDto): Observable<void> {
        return this.post(`/${ELIGIBILITY_REQUEST.EligibilityRequests}/${ELIGIBILITY_REQUEST_ENDPOINTS.CreateOrEdit}`, data);
    }

    deleteEligibilityRequest(id: string): Observable<void> {
        return this.delete(`/${ELIGIBILITY_REQUEST.EligibilityRequests}/${ELIGIBILITY_REQUEST_ENDPOINTS.Delete}?Id=${id}`);
    }

    generateEligibilityPdf(eligibilityRequestId: string): Observable<{ fileName: string; fileType?: string; fileToken: string }> {
        return this.post(
            `/${ELIGIBILITY_REQUEST.EligibilityRequests}/${ELIGIBILITY_REQUEST_ENDPOINTS.GenerateEligibilityPDF}?id=${eligibilityRequestId}`,
            {}
        ).pipe(
            map((response: any) => response?.result ?? response)
        );
    }

    // Eligibility Response APIs
    getEligibilityResponses(queryParams?: string): Observable<IPageResponse<IEligibilityResponse>> {
        return this.getPaginatedData<IEligibilityResponse>(
            ELIGIBILITY_RESPONSE.EligibilityResponses,
            ELIGIBILITY_RESPONSE_ENDPOINTS.GetAll,
            queryParams,
            'eligibilityResponse'
        );
    }

    getEligibilityResponseForView(id: string): Observable<IEligibilityResponse> {
        return this.get(`/${ELIGIBILITY_RESPONSE.EligibilityResponses}/${ELIGIBILITY_RESPONSE_ENDPOINTS.GetEligibilityResponseForView}?id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.eligibilityResponse || response?.eligibilityResponse || response?.result || response;
                })
            );
    }

    getEligibilityResponseForEdit(id: string): Observable<ICreateOrEditEligibilityResponseDto> {
        return this.get(`/${ELIGIBILITY_RESPONSE.EligibilityResponses}/${ELIGIBILITY_RESPONSE_ENDPOINTS.GetEligibilityResponseForEdit}?Id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.eligibilityResponse || response?.eligibilityResponse || response?.result || response;
                })
            );
    }

    createOrEditEligibilityResponse(data: ICreateOrEditEligibilityResponseDto): Observable<void> {
        return this.post(`/${ELIGIBILITY_RESPONSE.EligibilityResponses}/${ELIGIBILITY_RESPONSE_ENDPOINTS.CreateOrEdit}`, data);
    }

    deleteEligibilityResponse(id: string): Observable<void> {
        return this.delete(`/${ELIGIBILITY_RESPONSE.EligibilityResponses}/${ELIGIBILITY_RESPONSE_ENDPOINTS.Delete}?Id=${id}`);
    }

    // Eligibility Response Benefit APIs
    getEligibilityResponseBenefits(queryParams?: string): Observable<IPageResponse<IEligibilityResponseBenefit>> {
        return this.getPaginatedData<IEligibilityResponseBenefit>(
            ELIGIBILITY_RESPONSE_BENEFIT.EligibilityResponseBenefits,
            ELIGIBILITY_RESPONSE_BENEFIT_ENDPOINTS.GetAll,
            queryParams,
            'eligibilityResponseBenefit'
        );
    }

    getEligibilityResponseBenefitsByResponse(eligibilityResponseId: string, queryParams?: string): Observable<IPageResponse<IEligibilityResponseBenefit>> {
        const params = queryParams 
            ? `EligibilityResponseIdFilter=${eligibilityResponseId}&${queryParams}` 
            : `EligibilityResponseIdFilter=${eligibilityResponseId}`;
        return this.getPaginatedData<IEligibilityResponseBenefit>(
            ELIGIBILITY_RESPONSE_BENEFIT.EligibilityResponseBenefits,
            ELIGIBILITY_RESPONSE_BENEFIT_ENDPOINTS.GetAll,
            params,
            'eligibilityResponseBenefit'
        );
    }

    getEligibilityResponseBenefitForView(id: string): Observable<IEligibilityResponseBenefit> {
        return this.get(`/${ELIGIBILITY_RESPONSE_BENEFIT.EligibilityResponseBenefits}/${ELIGIBILITY_RESPONSE_BENEFIT_ENDPOINTS.GetEligibilityResponseBenefitForView}?id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.eligibilityResponseBenefit || response?.eligibilityResponseBenefit || response?.result || response;
                })
            );
    }

    getEligibilityResponseBenefitForEdit(id: string): Observable<ICreateOrEditEligibilityResponseBenefitDto> {
        return this.get(`/${ELIGIBILITY_RESPONSE_BENEFIT.EligibilityResponseBenefits}/${ELIGIBILITY_RESPONSE_BENEFIT_ENDPOINTS.GetEligibilityResponseBenefitForEdit}?Id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.eligibilityResponseBenefit || response?.eligibilityResponseBenefit || response?.result || response;
                })
            );
    }

    createOrEditEligibilityResponseBenefit(data: ICreateOrEditEligibilityResponseBenefitDto): Observable<void> {
        return this.post(`/${ELIGIBILITY_RESPONSE_BENEFIT.EligibilityResponseBenefits}/${ELIGIBILITY_RESPONSE_BENEFIT_ENDPOINTS.CreateOrEdit}`, data);
    }

    deleteEligibilityResponseBenefit(id: string): Observable<void> {
        return this.delete(`/${ELIGIBILITY_RESPONSE_BENEFIT.EligibilityResponseBenefits}/${ELIGIBILITY_RESPONSE_BENEFIT_ENDPOINTS.Delete}?Id=${id}`);
    }

    // Eligibility Request Status APIs
    getEligibilityRequestStatuses(queryParams?: string): Observable<IPageResponse<IEligibilityRequestStatus>> {
        return this.getPaginatedData<IEligibilityRequestStatus>(
            ELIGIBILITY_REQUEST_STATUS.EligibilityRequestStatuses,
            ELIGIBILITY_REQUEST_STATUS_ENDPOINTS.GetAll,
            queryParams,
            'eligibilityRequestStatus'
        );
    }

    getEligibilityRequestStatusForView(id: string): Observable<IEligibilityRequestStatus> {
        return this.get(`/${ELIGIBILITY_REQUEST_STATUS.EligibilityRequestStatuses}/${ELIGIBILITY_REQUEST_STATUS_ENDPOINTS.GetEligibilityRequestStatusForView}?id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.eligibilityRequestStatus || response?.eligibilityRequestStatus || response?.result || response;
                })
            );
    }

    getEligibilityRequestStatusForEdit(id: string): Observable<ICreateOrEditEligibilityRequestStatusDto> {
        return this.get(`/${ELIGIBILITY_REQUEST_STATUS.EligibilityRequestStatuses}/${ELIGIBILITY_REQUEST_STATUS_ENDPOINTS.GetEligibilityRequestStatusForEdit}?Id=${id}`)
            .pipe(
                map((response: any) => {
                    return response?.result?.eligibilityRequestStatus || response?.eligibilityRequestStatus || response?.result || response;
                })
            );
    }

    createOrEditEligibilityRequestStatus(data: ICreateOrEditEligibilityRequestStatusDto): Observable<void> {
        return this.post(`/${ELIGIBILITY_REQUEST_STATUS.EligibilityRequestStatuses}/${ELIGIBILITY_REQUEST_STATUS_ENDPOINTS.CreateOrEdit}`, data);
    }

    deleteEligibilityRequestStatus(id: string): Observable<void> {
        return this.delete(`/${ELIGIBILITY_REQUEST_STATUS.EligibilityRequestStatuses}/${ELIGIBILITY_REQUEST_STATUS_ENDPOINTS.Delete}?Id=${id}`);
    }

    getEligibilityRequestStatusCount(eligibilityRequestId: string): Observable<number> {
        return this.post(`/${ELIGIBILITY_REQUEST_STATUS.EligibilityRequestStatuses}/${ELIGIBILITY_REQUEST_STATUS_ENDPOINTS.Count}?eligibilityRequestId=${eligibilityRequestId}`, {})
            .pipe(
                map((response: any) => {
                    return response?.result !== undefined ? response.result : (response !== undefined ? response : 0);
                })
            );
    }

    getEligibilityResponseCount(eligibilityRequestId: string): Observable<number> {
        return this.post(`/${ELIGIBILITY_RESPONSE.EligibilityResponses}/${ELIGIBILITY_RESPONSE_ENDPOINTS.Count}?eligibilityRequestId=${eligibilityRequestId}`, {})
            .pipe(
                map((response: any) => {
                    return response?.result !== undefined ? response.result : (response !== undefined ? response : 0);
                })
            );
    }

    // Patient Note APIs
    getPatientNotes(patientId: string): Observable<any[]> {
        return this.get(`/patients/note?patientId=${patientId}`)
            .pipe(
                map((response: any) => {
                    return response?.result || response || [];
                })
            );
    }

    createPatientNote(data: { tenantId?: number; patientId: string; notes: string; id?: string }): Observable<any> {
        return this.post('/patients/note', data)
            .pipe(
                map((response: any) => {
                    return response?.result || response || data;
                })
            );
    }

    updatePatientNote(data: { tenantId?: number; patientId: string; notes: string; id: string }): Observable<any> {
        return this.put('/patients/note', data)
            .pipe(
                map((response: any) => {
                    return response?.result || response || data;
                })
            );
    }

    deletePatientNote(noteId: string): Observable<void> {
        return this.delete(`/patients/note/${noteId}`);
    }
}
